import { useField } from 'formik';
import { memo, useEffect, useState } from 'react';

import './PhoneNumberField.css';

const getDigits = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * Map stored `${codeDigits}${national}` → national digits for display sync from props.
 * Parents of this field always pass prefixed values (see handleChange / getBusinessPhone).
 */
const toNationalNumber = (value, countryCode = '+1') => {
  const digits = getDigits(value);
  const codeDigits = getDigits(countryCode);
  if (codeDigits && digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
    return digits.slice(codeDigits.length).slice(0, 10);
  }
  return digits.slice(0, 10);
};

/**
 * Digits from the visible national input. Never strips a leading "1" while typing —
 * the dial code is shown outside the field. Only strips on paste of 11+ digit values.
 */
const toNationalInputDigits = (raw, countryCode = '+1') => {
  let digits = getDigits(raw);
  const codeDigits = getDigits(countryCode);
  if (codeDigits && digits.length > 10 && digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length);
  }
  return digits.slice(0, 10);
};

const PhoneNumberInput = memo(
  ({
    id = 'phone',
    name = 'phone',
    value = '',
    countryCode = '+1',
    onChange,
    onBlur,
    hasError = false,
    placeholder = 'Enter phone number',
    className = '',
    disabled = false,
  }) => {
    const codeDigits = getDigits(countryCode) || '1';
    // Local national digits are the typing source of truth so keystrokes are never
    // re-interpreted as a country-code strip (which ate leading "1"s).
    const [national, setNational] = useState(() => toNationalNumber(value, countryCode));

    useEffect(() => {
      setNational(toNationalNumber(value, countryCode));
    }, [value, countryCode]);

    const handleChange = (event) => {
      if (disabled) return;
      const nextNational = toNationalInputDigits(event.target.value, countryCode);
      setNational(nextNational);
      onChange?.(nextNational ? `${codeDigits}${nextNational}` : '');
    };

    return (
      <div
        className={[
          'merchant-phone-input-wrap',
          hasError ? 'merchant-phone-input-wrap--error' : '',
          disabled ? 'merchant-phone-input-wrap--readonly' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="merchant-phone-input__prefix">{`+${codeDigits}`}</span>
        <input
          id={id}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder={placeholder}
          value={national}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          readOnly={disabled}
          className="merchant-phone-input__native"
        />
      </div>
    );
  },
);

PhoneNumberInput.displayName = 'PhoneNumberInput';

const FormikPhoneNumberField = memo(({ name = 'phone', label = 'Phone Number' }) => {
  const [field, meta, helpers] = useField(name);
  const hasError = meta.touched && meta.error;

  return (
    <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
      <label
        htmlFor={name}
        className="desktop:text-[16px] text-sm font-semibold leading-3.75 desktop:leading-5 text-merchant-text-secondary"
      >
        {label}
      </label>
      <PhoneNumberInput
        id={name}
        name={name}
        value={field.value || ''}
        onChange={(nextValue) => helpers.setValue(nextValue)}
        onBlur={() => helpers.setTouched(true)}
        hasError={hasError}
      />
      {hasError && <p className="desktop:text-[12px] text-[10px] text-error">{meta.error}</p>}
    </div>
  );
});

FormikPhoneNumberField.displayName = 'FormikPhoneNumberField';

const PhoneNumberField = memo(
  ({
    name = 'phone',
    label = 'Phone Number',
    value,
    countryCode = '+1',
    onChange,
    onBlur,
    hasError,
    className,
    disabled = false,
  }) => {
    if (typeof onChange === 'function') {
      return (
        <PhoneNumberInput
          id={name}
          name={name}
          value={value}
          countryCode={countryCode}
          onChange={onChange}
          onBlur={onBlur}
          hasError={hasError}
          className={className}
          disabled={disabled}
        />
      );
    }

    return <FormikPhoneNumberField name={name} label={label} />;
  },
);

PhoneNumberField.displayName = 'PhoneNumberField';

export { PhoneNumberInput, toNationalNumber };
export default PhoneNumberField;
